'use client';

import React, { useEffect, useState } from 'react';
import { useTranslation } from '@repo/ui/src/context/Translation/useTranslation';
import axios from 'axios';
import Flip from './Flip';
import buildImageSrc from '@repo/ui/src/functions/image-functions-aspect-ratio';
import TripleContent from '../triple-content/triple-content.component';
import { classNames } from '@repo/ui/src/utils/classNames';
import HeroWrapper from './HeroWrapper';
import ComponentWrapper from './ComponentWrapper';
import { ImageProps } from '@repo/ui/src/types';

interface FlipCounterProps {
  _meta?: any;
  titleText?: string;
  descriptionText?: string;
  alignment?: 'left' | 'center' | 'right';
  counterLabelText?: string;
  isHero?: boolean;
  backgroundImage?: ImageProps;
}

const FlipCounter: React.FC<FlipCounterProps> = ({
  titleText,
  descriptionText,
  alignment = 'center',
  counterLabelText,
  isHero = false,
  backgroundImage,
}) => {
  const { t } = useTranslation();
  const [cookMillions, setCookMillions] = useState(38);
  const [cooks, setCooks] = useState(0);

  useEffect(() => {
    axios
      .get('/api/cooks-count')
      .then((response) => {
        const count = Number(response.data?.count ?? 0);
        setCookMillions(Math.trunc(count / 1000000));
        setCooks(count);
      })
      .catch(() => {
        const fallback = 69854745;
        setCookMillions(Math.trunc(fallback / 1000000));
        setCooks(fallback);
      });
  }, []);

  const replaceTemplateVariables = (text: string) => {
    return text.replace(/\{\{cookMillions\}\}/g, cookMillions.toString());
  };

  const isLeftAlign = alignment === 'left';
  const isCenterAlign = alignment === 'center';
  const isRightAlign = alignment === 'right';
  const titleHeadingLevel = isHero ? 'h1' : 'h2';
  const layout = isHero ? 'vertical' : 'horizontal';
  const isVertical = layout === 'vertical';

  const containerClasses = classNames(
    'flex flex-col justify-center',
    isVertical ? 'gap-8' : 'lg:flex-row lg:gap-20 items-center',
    // Alignment specific classes
    isLeftAlign && 'items-start text-left xl:text-left',
    isCenterAlign && 'items-center text-center w-full',
    isRightAlign && 'items-end text-right xl:text-right w-full',
  );

  const headingNumber = titleHeadingLevel.replace('h', '');

  const titleClasses = classNames(
    'mb-5',
    `tra-text-web-heading-${headingNumber}`,
    'is-visible',
    'text-white',
  );

  const counterContainerClasses = classNames(
    'pt-12',
    isLeftAlign && 'xl:w-fit xl:ml-0',
    isCenterAlign && 'xl:w-fit xl:ml-auto',
    isRightAlign && 'w-full xl:mr-0',
  );

  const counterFlexClasses = classNames(
    'flex justify-center items-center',
    isLeftAlign && 'justify-start xl:justify-start',
    isCenterAlign && 'justify-center xl:justify-center',
    isRightAlign && 'justify-end xl:justify-end',
  );

  const counterLabelClasses = classNames(
    'text-[#969696] uppercase mt-2.5',
    'tra-text-web-heading-3',
    isLeftAlign && 'text-left xl:text-left',
    isCenterAlign && 'text-center xl:text-center',
    isRightAlign && 'text-right xl:text-right',
  );

  const FlipCounterContent = () => (
    <div className={containerClasses}>
      {(titleText || descriptionText) && (
        <div
          className={classNames(
            'tra-text-web-body-xl',
            isHero && 'lg:max-w-[735px]',
            !isHero && 'lg:max-w-[525px]',
            isLeftAlign && 'lg:text-left',
            isCenterAlign && 'md:text-center lg:text-left',
            isRightAlign && 'lg:text-right',
          )}
        >
          {titleText &&
            React.createElement(
              titleHeadingLevel as keyof JSX.IntrinsicElements,
              {
                'aria-label': 'customer cooks',
                className: titleClasses,
              },
              replaceTemplateVariables(titleText),
            )}
          {descriptionText && <TripleContent content={descriptionText} dark white={true} />}
        </div>
      )}
      <div>
        <div className={counterContainerClasses}>
          <div className={counterFlexClasses}>
            <div className={classNames(isLeftAlign && '[&>*]:justify-start')}>
              {cooks > 0 && <Flip value={cooks} />}
            </div>
          </div>
          <h3 className={counterLabelClasses}>{counterLabelText || t('CUSTOMER COOKS')}</h3>
        </div>
      </div>
    </div>
  );

  const imageUrl = buildImageSrc(backgroundImage);

  if (isHero) {
    return (
      <HeroWrapper alignment={alignment} backgroundImage={imageUrl}>
        <FlipCounterContent />
      </HeroWrapper>
    );
  } else {
    return (
      <ComponentWrapper backgroundImage={imageUrl}>
        <FlipCounterContent />
      </ComponentWrapper>
    );
  }
};

export default FlipCounter;
